import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const STORAGE_KEY = "nh_cookie_consent_v1";

/**
 * Minimal, first-party cookie/tracking consent banner.
 * NurseLinkHub uses only a first-party httpOnly session cookie (no third-party ad
 * tracking today), so this is primarily a privacy-transparency notice. When the
 * user accepts or dismisses, we persist a flag in localStorage and never nag again.
 */
export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) setOpen(true);
    } catch { /* localStorage disabled — hide silently */ }
  }, []);

  const dismiss = (choice) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, at: new Date().toISOString() })); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      data-testid="cookie-consent"
      className="fixed z-50 bottom-20 md:bottom-4 inset-x-3 md:inset-x-auto md:right-4 md:max-w-md bg-white border-2 border-black nh-brutal p-4 shadow-lg"
      style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 id="cookie-consent-title" className="text-base font-black tracking-tight">Cookies & privacy</h2>
        <button
          onClick={() => dismiss("dismissed")}
          data-testid="cookie-close"
          aria-label="Close cookie notice"
          className="text-black/40 hover:text-black -mr-1 -mt-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="mt-2 text-sm text-black/70 leading-relaxed">
        NurseLinkHub uses a single essential cookie to keep you logged in. We don't sell your data or run third-party ad trackers. See our{" "}
        <Link to="/privacy" data-testid="cookie-privacy-link" className="underline font-bold hover:text-[hsl(var(--nh-red))]">Privacy Policy</Link>.
      </p>
      <div className="mt-3 flex gap-2">
        <button
          data-testid="cookie-accept"
          onClick={() => dismiss("accepted")}
          className="flex-1 h-9 bg-black text-white font-bold text-sm uppercase tracking-tight border-2 border-black nh-hover-push"
        >
          Got it
        </button>
        <Link
          to="/privacy"
          data-testid="cookie-learn"
          className="flex-1 h-9 flex items-center justify-center bg-white text-black font-bold text-sm uppercase tracking-tight border-2 border-black nh-hover-push"
        >
          Learn more
        </Link>
      </div>
    </div>
  );
}
